/**
 * UI quad batcher — SpriteBatch's mechanics (shared 32B vertex layout, one VB/IB, subrange
 * uploads, batch-break on state change) specialized for UI: SDF rects via 'ui/ui-rect', MSDF
 * text, backdrop-blur cuts (UI-16, via UIBackdrop) and per-guid material pipelines (UI-19),
 * plus a scissor stack for overflow:hidden (scissor change breaks the batch). All coordinates
 * here are PHYSICAL pixels.
 *
 * Vertex reuse of SPRITE_VERTEX_LAYOUT slots: position=quad corner, uv=px-from-rect-center,
 * color=f16 RGBA, emission lane=f16 params (halfW, halfH, cornerRadius, borderWidth — the
 * borderWidth slot carries the acrylic noise amount for backdrop quads).
 */
import {
  GPUType,
  NULL_HANDLE,
  SPRITE_VERTEX_LAYOUT,
  SPRITE_VERTEX_SIZE,
  defineStruct,
  getCompiledShader,
  toHalf,
  type BufferHandle,
  type IRenderContext,
  type PipelineHandle,
  type ShaderHandle,
  type TextureHandle,
} from '@engine/render2d';
import type { GlyphQuad } from '../text/layoutText';
import type { Color4, GradientDecl } from '../types';
import { invert, isAffine, isIdentity, multiply, type Mat2D } from './mat2d';
import { UIBackdrop } from './UIBackdrop';

/** Named uniform layout for ui/ui-gradient.slang — field names are the setUniform keys and the
 *  byte layout mirrors the shader's ConstantBuffer (mat4 + vec4s, trivially aligned). */
const UI_GRADIENT_LAYOUT = defineStruct('UIGradientUniforms', {
  uViewProjection: GPUType.f32().mat4x4(),
  uGradHeader: GPUType.f32().vec4(),
  uGradColor0: GPUType.f32().vec4(),
  uGradColor1: GPUType.f32().vec4(),
  uGradColor2: GPUType.f32().vec4(),
  uGradColor3: GPUType.f32().vec4(),
  uGradColor4: GPUType.f32().vec4(),
  uGradColor5: GPUType.f32().vec4(),
  uGradPosA: GPUType.f32().vec4(),
  uGradPosB: GPUType.f32().vec4(),
  uGradShape: GPUType.f32().vec4(), // radial/conic center + params in local px
  uClipRect: GPUType.f32().vec4(), // rounded overflow-clip footprint (cx, cy, halfX, halfY) px
  uClipParams: GPUType.f32().vec4(), // (radius px, enabled, 0, 0) — matches the default layout's slots
  uHom0: GPUType.f32().vec4(), // UI-17c 3D: inverse homography col for screenX + flag in .w
  uHom1: GPUType.f32().vec4(), // col for screenY
  uHom2: GPUType.f32().vec4(), // translation col
});

/** Max stops ui-gradient.slang supports — keep in sync with the parser's GRADIENT_MAX_STOPS. */
const GRADIENT_STOPS = 6;

const MAX_QUADS = 4096;
const VERTS_PER_QUAD = 4;
const INDICES_PER_QUAD = 6;

export interface UIScissorRect {
  x: number;
  y: number;
  width: number;
  height: number;
  /**
   * Corner radius (physical px) of a rounded overflow-clip container's PADDING box. The x/y/w/h
   * bounds are still enforced by the hardware scissor; this only carves the corners in-shader
   * (see the UI shaders' `uiClip`). Absent / 0 → plain rectangular clip, no shader cost.
   */
  radius?: number;
}

export interface UIBatchOptions {
  /**
   * Decoded font atlas image by font guid (host-owned cache; editor/player decode the atlas PNG).
   * UIBatch creates + caches the GPU texture on ITS context. Null/absent → text is skipped.
   */
  resolveFontAtlas?: (fontGuid: string) => ImageBitmap | HTMLImageElement | null;
  /**
   * Compiled UI material WGSL by shader-asset guid (UI-19; host-owned cache — editor reads the
   * import cache, player the ui.json bundle). SYNC, same retry idiom as fonts: null while the
   * host is still loading → the node draws as a plain rect this frame and retries next.
   */
  resolveMaterialWGSL?: (materialGuid: string) => string | null;
  /**
   * Decoded background image by image-asset guid (`background-image: asset()`, CSS wave 2).
   * Same host-owned-cache/retry idiom as fonts; null while decoding → skipped this frame.
   */
  resolveImage?: (imageGuid: string) => ImageBitmap | HTMLImageElement | null;
}

type MaterialPipelines = { shader: ShaderHandle; aa: PipelineHandle; hard: PipelineHandle };

export class UIBatch {
  private readonly ctx: IRenderContext;

  private vertexBuffer: BufferHandle = NULL_HANDLE;
  private indexBuffer: BufferHandle = NULL_HANDLE;
  private rectShader: ShaderHandle = NULL_HANDLE;
  /** Spec-constant pipeline VARIANTS off one shader module (ANTIALIAS @id(0)) — the same
   *  mechanism UI materials extend later; never flag-packing into uniform slots. */
  private rectPipeline: PipelineHandle = NULL_HANDLE;
  private rectPipelineHard: PipelineHandle = NULL_HANDLE;
  private textShader: ShaderHandle = NULL_HANDLE;
  private textPipeline: PipelineHandle = NULL_HANDLE;
  private backdropShader: ShaderHandle = NULL_HANDLE;
  private backdropPipeline: PipelineHandle = NULL_HANDLE;
  private backdropPipelineHard: PipelineHandle = NULL_HANDLE;
  private shadowShader: ShaderHandle = NULL_HANDLE;
  private shadowPipeline: PipelineHandle = NULL_HANDLE;
  private shadowPipelineInset: PipelineHandle = NULL_HANDLE;
  private shadowInset = false; // whether the current shadow batch is an inner (inset) shadow
  private gradientShader: ShaderHandle = NULL_HANDLE;
  private gradientPipeline: PipelineHandle = NULL_HANDLE;
  private gradientPipelineHard: PipelineHandle = NULL_HANDLE;
  private imageShader: ShaderHandle = NULL_HANDLE;
  private imagePipeline: PipelineHandle = NULL_HANDLE;
  private imagePipelineHard: PipelineHandle = NULL_HANDLE;

  private readonly resolveFontAtlas: ((fontGuid: string) => ImageBitmap | HTMLImageElement | null) | null;
  private readonly resolveMaterialWGSL: ((materialGuid: string) => string | null) | null;
  private readonly resolveImage: ((imageGuid: string) => ImageBitmap | HTMLImageElement | null) | null;
  private readonly fontTextures = new Map<string, TextureHandle>();
  private readonly imageTextures = new Map<string, { texture: TextureHandle; width: number; height: number }>();
  /** Per-material pipeline pairs off the host-resolved WGSL ('failed' = compile error, warn-once). */
  private readonly materialPipelines = new Map<string, MaterialPipelines | 'failed'>();
  /** Blur-chain owner, created on the first acrylic node (UI-16). */
  private backdrop: UIBackdrop | null = null;
  /** State of the PENDING batch (flush consumes it; setters flush before changing). */
  private mode: 'rect' | 'text' | 'backdrop' | 'material' | 'shadow' | 'gradient' | 'image' = 'rect';
  private fontTexture: TextureHandle = NULL_HANDLE;
  private fontPxRange = 4;
  private backdropTexture: TextureHandle = NULL_HANDLE;
  private imageTexture: TextureHandle = NULL_HANDLE;
  private materialGuid = '';
  private materialTime = 0;
  /** Per-flush gradient uniform payload (one draw per gradient rect — flushed immediately). */
  private readonly gradHeader = new Float32Array(4);
  private readonly gradColors = new Float32Array(GRADIENT_STOPS * 4);
  private readonly gradPos = new Float32Array(8);
  private readonly gradShape = new Float32Array(4);
  /** Rounded overflow-clip uniforms, rewritten per flush from the active scissor's radius. */
  private readonly clipRectU = new Float32Array(4); // (cx, cy, halfX, halfY)
  private readonly clipParamsU = new Float32Array(4); // (radius, enabled, 0, 0)
  private static readonly CLIP_OFF = new Float32Array([0, 0, 0, 0]);
  private _warnedNoFontTexture = false;

  private readonly vertexData: Float32Array;
  private readonly vertexDataU16: Uint16Array;

  private quadCount = 0;
  private batchStart = 0;
  private batching = false;
  private readonly vp = new Float32Array(16);
  /** Viewport size for this begin/end cycle (physical px) — the backdrop blur chain needs it. */
  private viewWidth = 0;
  private viewHeight = 0;
  /** Scissor active for the CURRENT batch (null = full viewport). */
  private scissor: UIScissorRect | null = null;
  /** Transform applied to quad corner POSITIONS (UI-17). null = identity. Affine (g=h=0) is the fast
   *  path — CPU-transforms corners, NOT a batch break. Perspective (UI-17c) projects corners AND sets
   *  a per-node inverse-homography uniform (batch break) so the fragment recovers `local` exactly. */
  private transform: Mat2D | null = null;
  /** Active inverse-homography uniform columns for perspective quads (null = affine/none, flag off). */
  private hom: Float32Array | null = null;
  private readonly homU0 = new Float32Array(4);
  private readonly homU1 = new Float32Array(4);
  private readonly homU2 = new Float32Array(4);
  private homTransform: Mat2D | null = null; // the (transform, cx, cy) the current `hom` was built for
  private homCx = 0;
  private homCy = 0;
  private static readonly HOM_OFF = new Float32Array([0, 0, 0, 0]); // flag (.w) off → shader uses interpolated local
  /** Rect-edge antialiasing for the CURRENT batch (per-canvas toggle; change breaks the batch). */
  private antialias = true;

  private _drawCalls = 0;

  constructor(ctx: IRenderContext, options?: UIBatchOptions) {
    this.ctx = ctx;
    this.resolveFontAtlas = options?.resolveFontAtlas ?? null;
    this.resolveMaterialWGSL = options?.resolveMaterialWGSL ?? null;
    this.resolveImage = options?.resolveImage ?? null;
    const bytes = MAX_QUADS * VERTS_PER_QUAD * SPRITE_VERTEX_SIZE;
    const buffer = new ArrayBuffer(bytes);
    this.vertexData = new Float32Array(buffer);
    this.vertexDataU16 = new Uint16Array(buffer);
    this.initResources();
  }

  private initResources(): void {
    const { ctx } = this;
    const wgsl = getCompiledShader('ui/ui-rect');
    this.rectShader = ctx.createShader(wgsl, wgsl);
    // '0' = ANTIALIAS ([vk::constant_id(0)] — slang mangles override NAMES, @id is stable)
    this.rectPipeline = ctx.createPipeline({
      shader: this.rectShader,
      vertexBuffers: [SPRITE_VERTEX_LAYOUT],
      colorTargets: [{ format: ctx.getSwapChainFormat() }],
      primitive: { usesTextures: false },
      constants: { '0': 1 },
      label: 'UIBatch-rect',
    });
    this.rectPipelineHard = ctx.createPipeline({
      shader: this.rectShader,
      vertexBuffers: [SPRITE_VERTEX_LAYOUT],
      colorTargets: [{ format: ctx.getSwapChainFormat() }],
      primitive: { usesTextures: false },
      constants: { '0': 0 },
      label: 'UIBatch-rect-hard',
    });

    this.vertexBuffer = ctx.createBuffer({
      size: MAX_QUADS * VERTS_PER_QUAD * SPRITE_VERTEX_SIZE,
      usage: ['vertex'],
      label: 'UIBatch-vb',
    });

    const indices = new Uint16Array(MAX_QUADS * INDICES_PER_QUAD);
    for (let i = 0; i < MAX_QUADS; i++) {
      const v = i * 4;
      const o = i * 6;
      indices[o + 0] = v + 0;
      indices[o + 1] = v + 1;
      indices[o + 2] = v + 2;
      indices[o + 3] = v + 2;
      indices[o + 4] = v + 3;
      indices[o + 5] = v + 0;
    }
    this.indexBuffer = ctx.createBuffer({
      size: indices.byteLength,
      usage: ['index'],
      data: indices,
      label: 'UIBatch-ib',
    });
  }

  /** Reset the buffer cursor. Once per frame, before any begin/end cycles. */
  startFrame(): void {
    this.quadCount = 0;
    this.batchStart = 0;
  }

  begin(viewProjection: Float32Array, viewWidth: number, viewHeight: number): void {
    if (this.batching) throw new Error('UIBatch.begin() without end()');
    this.batching = true;
    this.batchStart = this.quadCount;
    this.vp.set(viewProjection);
    this.viewWidth = viewWidth;
    this.viewHeight = viewHeight;
    this.scissor = null;
    this.transform = null;
    this.hom = null;
    this.homTransform = null;
    this.mode = 'rect';
    this._drawCalls = 0;
  }

  end(): void {
    if (!this.batching) throw new Error('UIBatch.end() without begin()');
    this.flush();
    // leave the pass with the scissor reset so later passes aren't clipped
    if (this.scissor) this.ctx.disableScissor();
    this.batching = false;
  }

  /** Rect-edge AA on/off (canvas `antialias`). Breaks the batch on change. */
  setAntialias(on: boolean): void {
    if (this.antialias === on) return;
    this.flush();
    this.antialias = on;
  }

  /** Set the corner transform (UI-17). null/identity = axis-aligned fast path. No batch break. */
  setTransform(m: Mat2D | null): void {
    this.transform = m && !isIdentity(m) ? m : null;
  }

  /** Set the active scissor (physical px, null = none). Breaks the batch on change. */
  setScissor(rect: UIScissorRect | null): void {
    const cur = this.scissor;
    const same =
      cur === rect ||
      (!!cur &&
        !!rect &&
        cur.x === rect.x &&
        cur.y === rect.y &&
        cur.width === rect.width &&
        cur.height === rect.height &&
        (cur.radius ?? 0) === (rect.radius ?? 0));
    if (same) return;
    this.flush();
    this.scissor = rect ? { ...rect } : null;
  }

  /** Solid / rounded / bordered rect. x/y/w/h in physical px (already snapped by the caller). */
  rect(x: number, y: number, w: number, h: number, color: Color4, cornerRadius = 0, borderWidth = 0): void {
    if (!this.batching) throw new Error('UIBatch.rect() without begin()');
    if (w <= 0 || h <= 0 || color[3] <= 0) return;
    // degenerate scissor = fully clipped
    if (this.scissor && (this.scissor.width <= 0 || this.scissor.height <= 0)) return;
    if (this.mode !== 'rect') {
      this.flush();
      this.mode = 'rect';
    }
    this.pushQuad(x, y, w, h, color, cornerRadius, borderWidth);
  }

  /**
   * Backdrop blur cut (UI-16): flush what's pending, blur the backbuffer, then draw ONE quad
   * that replaces the rounded-rect footprint with the blurred content (+ acrylic grain).
   * No-ops gracefully when the host can't copy the swapchain.
   */
  backdropRect(
    x: number,
    y: number,
    w: number,
    h: number,
    cornerRadius: number,
    blurPx: number,
    noise: number,
    opacity: number,
  ): void {
    if (!this.batching) throw new Error('UIBatch.backdropRect() without begin()');
    if (w <= 0 || h <= 0 || opacity <= 0 || blurPx <= 0) return;
    if (this.scissor && (this.scissor.width <= 0 || this.scissor.height <= 0)) return;

    this.flush(); // the blur must see everything painted before this node
    if (this.backdropPipeline === NULL_HANDLE) this.initBackdropPipelines();
    this.backdrop ??= new UIBackdrop(this.ctx);
    const blurred = this.backdrop.blur(this.viewWidth, this.viewHeight, blurPx);
    if (blurred === NULL_HANDLE) return; // degrade: the node's translucent bg still paints

    // one-quad batch: the blurred texture is shared scratch — flush before the next cut reuses it
    this.mode = 'backdrop';
    this.backdropTexture = blurred;
    this.pushQuad(x, y, w, h, [0, 0, 0, opacity], cornerRadius, noise);
    this.flush();
  }

  /** UI material rect (UI-19): the per-guid material pipeline shades the rounded rect. */
  materialRect(
    x: number,
    y: number,
    w: number,
    h: number,
    color: Color4,
    cornerRadius: number,
    borderWidth: number,
    materialGuid: string,
    time: number,
  ): void {
    if (!this.batching) throw new Error('UIBatch.materialRect() without begin()');
    if (w <= 0 || h <= 0) return;
    if (this.scissor && (this.scissor.width <= 0 || this.scissor.height <= 0)) return;

    if (!this.materialPipelineFor(materialGuid)) {
      // WGSL not resolved yet (host still loading) or failed — plain fill this frame
      this.rect(x, y, w, h, color, cornerRadius, 0);
      return;
    }
    if (this.mode !== 'material' || this.materialGuid !== materialGuid || this.materialTime !== time) {
      this.flush();
      this.mode = 'material';
      this.materialGuid = materialGuid;
      this.materialTime = time;
    }
    this.pushQuad(x, y, w, h, color, cornerRadius, borderWidth);
  }

  /**
   * Drop shadow (CSS wave 2): a soft rounded-rect quad UNDER the node — the quad is the rect
   * inflated by blur+spread and shifted by the offset; params carry the SDF target (original
   * half-size + spread) and the blur. Batches with other shadows (no per-draw uniforms).
   * All values physical px; color alpha already opacity-multiplied.
   */
  shadowRect(
    x: number,
    y: number,
    w: number,
    h: number,
    cornerRadius: number,
    offsetX: number,
    offsetY: number,
    blur: number,
    spread: number,
    color: Color4,
    inset = false,
  ): void {
    if (!this.batching) throw new Error('UIBatch.shadowRect() without begin()');
    if (w <= 0 || h <= 0 || color[3] <= 0) return;
    if (this.scissor && (this.scissor.width <= 0 || this.scissor.height <= 0)) return;
    if (this.shadowPipeline === NULL_HANDLE) this.initShadowPipeline();
    if (this.mode !== 'shadow' || this.shadowInset !== inset) {
      this.flush();
      this.mode = 'shadow';
      this.shadowInset = inset;
    }
    if (inset) {
      // inner shadow: drawn on the node's OWN rect, blur = falloff (offset/spread are v1.1)
      this.pushQuadRaw(x, y, w, h, color, w / 2, h / 2, cornerRadius, blur);
      return;
    }
    const pad = spread + blur;
    this.pushQuadRaw(
      x + offsetX - pad,
      y + offsetY - pad,
      w + 2 * pad,
      h + 2 * pad,
      color,
      w / 2 + spread,
      h / 2 + spread,
      cornerRadius > 0 ? cornerRadius + spread : 0,
      blur,
    );
  }

  /** Linear-gradient fill (CSS wave 2). One draw per rect — the stops ride per-draw uniforms. */
  gradientRect(
    x: number,
    y: number,
    w: number,
    h: number,
    cornerRadius: number,
    gradient: GradientDecl,
    opacity: number,
  ): void {
    if (!this.batching) throw new Error('UIBatch.gradientRect() without begin()');
    if (w <= 0 || h <= 0 || opacity <= 0 || gradient.stops.length < 2) return;
    if (this.scissor && (this.scissor.width <= 0 || this.scissor.height <= 0)) return;
    if (this.gradientPipeline === NULL_HANDLE) this.initGradientPipelines();

    this.flush();
    this.mode = 'gradient';

    const kind = gradient.kind ?? 'linear';
    const cxPx = ((gradient.cx ?? 0.5) - 0.5) * w; // center offset in local px (from rect center)
    const cyPx = ((gradient.cy ?? 0.5) - 0.5) * h;
    if (kind === 'radial') {
      this.gradHeader[3] = 1;
      const rx = gradient.shape === 'circle' ? Math.max(w, h) / 2 : w / 2;
      const ry = gradient.shape === 'circle' ? Math.max(w, h) / 2 : h / 2;
      this.gradShape[0] = cxPx;
      this.gradShape[1] = cyPx;
      this.gradShape[2] = 1 / Math.max(1e-4, rx); // inverse radii → shader multiplies
      this.gradShape[3] = 1 / Math.max(1e-4, ry);
    } else if (kind === 'conic') {
      this.gradHeader[3] = 2;
      this.gradShape[0] = cxPx;
      this.gradShape[1] = cyPx;
      this.gradShape[2] = (gradient.angleDeg * Math.PI) / 180; // start angle (rad)
      this.gradShape[3] = 0;
    } else {
      this.gradHeader[3] = 0;
      // CSS angle (0 = to top, 90 = to right) → y-down direction, pre-divided by the half
      // gradient-line length (line length = |w·sin| + |h·cos|) so the shader's t is one dot().
      const rad = (gradient.angleDeg * Math.PI) / 180;
      const dx = Math.sin(rad);
      const dy = -Math.cos(rad);
      const halfLen = Math.max(1e-4, (Math.abs(w * dx) + Math.abs(h * dy)) / 2);
      this.gradHeader[0] = dx / halfLen;
      this.gradHeader[1] = dy / halfLen;
    }
    this.gradHeader[2] = Math.min(gradient.stops.length, GRADIENT_STOPS);
    for (let i = 0; i < GRADIENT_STOPS; i++) {
      const stop = gradient.stops[Math.min(i, gradient.stops.length - 1)]!;
      this.gradColors.set(stop.color, i * 4);
      this.gradPos[i] = stop.pos;
    }

    this.pushQuad(x, y, w, h, [1, 1, 1, opacity], cornerRadius, 0);
    this.flush(); // the uniform payload is per-flush state
  }

  /**
   * Background image (CSS wave 2): whole-rect stretch or 9-slice frame. `srcInsets` are texel
   * insets [t,r,b,l] in the SOURCE image, `destInsets` the matching border widths in physical
   * px (paint pre-scales). Skipped silently while the host is still decoding (font idiom).
   */
  imageRect(
    x: number,
    y: number,
    w: number,
    h: number,
    tint: Color4,
    cornerRadius: number,
    imageGuid: string,
    srcInsets: readonly [number, number, number, number] | null,
    destInsets: readonly [number, number, number, number] | null,
  ): void {
    if (!this.batching) throw new Error('UIBatch.imageRect() without begin()');
    if (w <= 0 || h <= 0 || tint[3] <= 0) return;
    if (this.scissor && (this.scissor.width <= 0 || this.scissor.height <= 0)) return;

    const entry = this.imageTextureFor(imageGuid);
    if (!entry) return; // not decoded yet — retry next frame
    if (this.imagePipeline === NULL_HANDLE) this.initImagePipelines();
    if (this.mode !== 'image' || this.imageTexture !== entry.texture) {
      this.flush();
      this.mode = 'image';
      this.imageTexture = entry.texture;
    }

    if (!srcInsets || !destInsets) {
      this.pushQuadUV(x, y, w, h, 0, 0, 1, 1, tint, w / 2, h / 2, cornerRadius);
      return;
    }

    // 9-slice: 3×3 grid; corners fixed, edges stretch one axis, center stretches both.
    // cornerRadius is ignored for sliced frames (params radius 0 ⇒ coverage saturates).
    const [st, sr, sb, sl] = srcInsets;
    const [dt, dr, db, dl] = destInsets;
    const xs = [x, x + dl, x + w - dr, x + w];
    const ys = [y, y + dt, y + h - db, y + h];
    const us = [0, sl / entry.width, 1 - sr / entry.width, 1];
    const vs = [0, st / entry.height, 1 - sb / entry.height, 1];
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 3; col++) {
        const qw = xs[col + 1]! - xs[col]!;
        const qh = ys[row + 1]! - ys[row]!;
        if (qw <= 0 || qh <= 0) continue;
        this.pushQuadUV(xs[col]!, ys[row]!, qw, qh, us[col]!, vs[row]!, us[col + 1]!, vs[row + 1]!, tint, qw / 2, qh / 2, 0);
      }
    }
  }

  /** Shared quad writer: params lane = (halfW, halfH, p2, p3) — p3 is borderWidth for
   *  rect/material quads and the acrylic noise amount for backdrop quads. */
  private pushQuad(x: number, y: number, w: number, h: number, color: Color4, p2v: number, p3v: number): void {
    this.pushQuadRaw(x, y, w, h, color, w / 2, h / 2, p2v, p3v);
  }

  /** Quad writer with an explicit params lane (shadow quads: SDF half ≠ quad half). The uv
   *  lane carries px-from-quad-center (the SDF shaders' `local`). */
  private pushQuadRaw(
    x: number,
    y: number,
    w: number,
    h: number,
    color: Color4,
    p0v: number,
    p1v: number,
    p2v: number,
    p3v: number,
  ): void {
    if (this.quadCount >= MAX_QUADS) this.flush();

    const floatsPerVertex = SPRITE_VERTEX_SIZE / 4;
    const data = this.vertexData;
    const u16 = this.vertexDataU16;
    const hw = w / 2;
    const hh = h / 2;

    const rh = toHalf(color[0]);
    const gh = toHalf(color[1]);
    const bh = toHalf(color[2]);
    const ah = toHalf(color[3]);
    const p0 = toHalf(p0v);
    const p1 = toHalf(p1v);
    const p2 = toHalf(p2v);
    const p3 = toHalf(p3v);

    const t = this.transform;
    const persp = t !== null && !isAffine(t);
    if (persp) {
      if (!this.ensureHomography(x + hw, y + hh)) return; // degenerate projection → skip the quad
    } else {
      this.clearHomography();
    }
    let i = this.quadCount * VERTS_PER_QUAD * floatsPerVertex;
    const corners: readonly [number, number, number, number][] = [
      [x, y, -hw, -hh],
      [x + w, y, hw, -hh],
      [x + w, y + h, hw, hh],
      [x, y + h, -hw, hh],
    ];
    for (const [px, py, lx, ly] of corners) {
      // transform POSITION only (local/SDF params stay in the node's own frame); perspective divides.
      if (persp) {
        const pw = t!.g * px + t!.h * py + 1;
        const iw = pw !== 0 ? 1 / pw : 0;
        data[i++] = (t!.a * px + t!.c * py + t!.e) * iw;
        data[i++] = (t!.b * px + t!.d * py + t!.f) * iw;
      } else {
        data[i++] = t ? t.a * px + t.c * py + t.e : px;
        data[i++] = t ? t.b * px + t.d * py + t.f : py;
      }
      data[i++] = lx;
      data[i++] = ly;
      let o = i * 2;
      u16[o++] = rh;
      u16[o++] = gh;
      u16[o++] = bh;
      u16[o++] = ah;
      u16[o++] = p0;
      u16[o++] = p1;
      u16[o++] = p2;
      u16[o++] = p3;
      i += 4; // color + params consume 4 f32 slots
    }
    this.quadCount++;
  }

  /** Quad writer with an explicit TEXTURE uv rect in the uv lane (image quads). */
  private pushQuadUV(
    x: number,
    y: number,
    w: number,
    h: number,
    u0: number,
    v0: number,
    u1: number,
    v1: number,
    color: Color4,
    p0v: number,
    p1v: number,
    p2v: number,
  ): void {
    if (this.quadCount >= MAX_QUADS) this.flush();

    const floatsPerVertex = SPRITE_VERTEX_SIZE / 4;
    const data = this.vertexData;
    const u16 = this.vertexDataU16;

    const rh = toHalf(color[0]);
    const gh = toHalf(color[1]);
    const bh = toHalf(color[2]);
    const ah = toHalf(color[3]);
    const p0 = toHalf(p0v);
    const p1 = toHalf(p1v);
    const p2 = toHalf(p2v);

    const t = this.transform;
    const persp = t !== null && !isAffine(t);
    if (persp) {
      if (!this.ensureHomography(x + w / 2, y + h / 2)) return;
    } else {
      this.clearHomography();
    }
    let i = this.quadCount * VERTS_PER_QUAD * floatsPerVertex;
    const verts: readonly [number, number, number, number][] = [
      [x, y, u0, v0],
      [x + w, y, u1, v0],
      [x + w, y + h, u1, v1],
      [x, y + h, u0, v1],
    ];
    for (const [px, py, u, v] of verts) {
      if (persp) {
        const pw = t!.g * px + t!.h * py + 1;
        const iw = pw !== 0 ? 1 / pw : 0;
        data[i++] = (t!.a * px + t!.c * py + t!.e) * iw;
        data[i++] = (t!.b * px + t!.d * py + t!.f) * iw;
      } else {
        data[i++] = t ? t.a * px + t.c * py + t.e : px;
        data[i++] = t ? t.b * px + t.d * py + t.f : py;
      }
      data[i++] = u;
      data[i++] = v;
      let o = i * 2;
      u16[o++] = rh;
      u16[o++] = gh;
      u16[o++] = bh;
      u16[o++] = ah;
      u16[o++] = p0;
      u16[o++] = p1;
      u16[o++] = p2;
      u16[o++] = 0;
      i += 4;
    }
    this.quadCount++;
  }

  /** MSDF glyph run (see UIPaintTarget.glyphRun): quads logical relative to a physical origin. */
  glyphRun(
    quads: readonly GlyphQuad[],
    originX: number,
    originY: number,
    scale: number,
    color: Color4,
    fontGuid: string,
    distanceRange: number,
  ): void {
    if (!this.batching) throw new Error('UIBatch.glyphRun() without begin()');
    if (!quads.length || color[3] <= 0) return;
    if (this.scissor && (this.scissor.width <= 0 || this.scissor.height <= 0)) return;

    const texture = this.fontTextureFor(fontGuid);
    if (texture === NULL_HANDLE) {
      if (!this._warnedNoFontTexture) {
        this._warnedNoFontTexture = true;
        console.warn(`[ui] no atlas image for font '${fontGuid}' — text skipped`);
      }
      return;
    }
    if (this.textPipeline === NULL_HANDLE) this.initTextPipeline();
    if (this.mode !== 'text' || this.fontTexture !== texture || this.fontPxRange !== distanceRange) {
      this.flush();
      this.mode = 'text';
      this.fontTexture = texture;
      this.fontPxRange = distanceRange;
    }

    const floatsPerVertex = SPRITE_VERTEX_SIZE / 4;
    const data = this.vertexData;
    const u16 = this.vertexDataU16;
    const rh = toHalf(color[0]);
    const gh = toHalf(color[1]);
    const bh = toHalf(color[2]);
    const ah = toHalf(color[3]);

    const t = this.transform;
    // Text under a 3D transform projects its glyph corners (position-correct tilt); the atlas uv
    // interpolates linearly (glyphs are small → negligible warp), so text needs no homography uniform.
    const persp = t !== null && !isAffine(t);
    for (const q of quads) {
      if (this.quadCount >= MAX_QUADS) this.flush();
      const x0 = originX + q.x * scale;
      const y0 = originY + q.y * scale;
      const x1 = x0 + q.w * scale;
      const y1 = y0 + q.h * scale;
      let i = this.quadCount * VERTS_PER_QUAD * floatsPerVertex;
      const verts: readonly [number, number, number, number][] = [
        [x0, y0, q.u0, q.v0],
        [x1, y0, q.u1, q.v0],
        [x1, y1, q.u1, q.v1],
        [x0, y1, q.u0, q.v1],
      ];
      for (const [px, py, u, v] of verts) {
        if (persp) {
          const pw = t!.g * px + t!.h * py + 1;
          const iw = pw !== 0 ? 1 / pw : 0;
          data[i++] = (t!.a * px + t!.c * py + t!.e) * iw;
          data[i++] = (t!.b * px + t!.d * py + t!.f) * iw;
        } else {
          data[i++] = t ? t.a * px + t.c * py + t.e : px;
          data[i++] = t ? t.b * px + t.d * py + t.f : py;
        }
        data[i++] = u;
        data[i++] = v;
        let o = i * 2;
        u16[o++] = rh;
        u16[o++] = gh;
        u16[o++] = bh;
        u16[o++] = ah;
        u16[o++] = 0;
        u16[o++] = 0;
        u16[o++] = 0;
        u16[o++] = 0;
        i += 4;
      }
      this.quadCount++;
    }
  }

  /** GPU texture for a font atlas, created on THIS context from the host-provided bitmap. */
  private fontTextureFor(fontGuid: string): TextureHandle {
    const cached = this.fontTextures.get(fontGuid);
    if (cached !== undefined) return cached;
    const bitmap = this.resolveFontAtlas?.(fontGuid) ?? null;
    if (!bitmap) return NULL_HANDLE; // NOT cached — the host may still be decoding; retry next frame
    // createTextureFromImage uploads the bitmap (copy-dst+render-target usage + copyExternalImageToTexture).
    // MSDF stays LINEAR (no sRGB) — the RGB channels are distance values, not colors.
    const handle = this.ctx.createTextureFromImage(bitmap, { label: `ui-font-${fontGuid}` });
    this.fontTextures.set(fontGuid, handle);
    return handle;
  }

  /** GPU texture (+ source dims for 9-slice UVs) for a background image — same idiom as fonts. */
  private imageTextureFor(imageGuid: string): { texture: TextureHandle; width: number; height: number } | null {
    const cached = this.imageTextures.get(imageGuid);
    if (cached !== undefined) return cached;
    const bitmap = this.resolveImage?.(imageGuid) ?? null;
    if (!bitmap) return null; // NOT cached — retry next frame
    const texture = this.ctx.createTextureFromImage(bitmap, { label: `ui-image-${imageGuid}` });
    const entry = { texture, width: bitmap.width, height: bitmap.height };
    this.imageTextures.set(imageGuid, entry);
    return entry;
  }

  private initTextPipeline(): void {
    const wgsl = getCompiledShader('ui/text-msdf');
    this.textShader = this.ctx.createShader(wgsl, wgsl);
    this.textPipeline = this.ctx.createPipeline({
      shader: this.textShader,
      vertexBuffers: [SPRITE_VERTEX_LAYOUT],
      colorTargets: [{ format: this.ctx.getSwapChainFormat() }],
      primitive: { usesTextures: true, textureCount: 1 },
      label: 'UIBatch-text',
    });
  }

  private initBackdropPipelines(): void {
    const wgsl = getCompiledShader('ui/ui-backdrop');
    this.backdropShader = this.ctx.createShader(wgsl, wgsl);
    const make = (aa: number, label: string) =>
      this.ctx.createPipeline({
        shader: this.backdropShader,
        vertexBuffers: [SPRITE_VERTEX_LAYOUT],
        colorTargets: [{ format: this.ctx.getSwapChainFormat() }],
        primitive: { usesTextures: true, textureCount: 1 },
        constants: { '0': aa }, // ANTIALIAS @id(0), same variant pair as ui-rect
        label,
      });
    this.backdropPipeline = make(1, 'UIBatch-backdrop');
    this.backdropPipelineHard = make(0, 'UIBatch-backdrop-hard');
  }

  private initShadowPipeline(): void {
    const wgsl = getCompiledShader('ui/ui-shadow');
    this.shadowShader = this.ctx.createShader(wgsl, wgsl);
    // no AA variant — the blur band IS the edge treatment; '0' = INSET spec-const (outer vs inner)
    const make = (inset: number, label: string) =>
      this.ctx.createPipeline({
        shader: this.shadowShader,
        vertexBuffers: [SPRITE_VERTEX_LAYOUT],
        colorTargets: [{ format: this.ctx.getSwapChainFormat() }],
        primitive: { usesTextures: false },
        constants: { '0': inset },
        label,
      });
    this.shadowPipeline = make(0, 'UIBatch-shadow');
    this.shadowPipelineInset = make(1, 'UIBatch-shadow-inset');
  }

  private initGradientPipelines(): void {
    const wgsl = getCompiledShader('ui/ui-gradient');
    this.gradientShader = this.ctx.createShader(wgsl, wgsl);
    const make = (aa: number, label: string) =>
      this.ctx.createPipeline({
        shader: this.gradientShader,
        vertexBuffers: [SPRITE_VERTEX_LAYOUT],
        colorTargets: [{ format: this.ctx.getSwapChainFormat() }],
        primitive: { usesTextures: false },
        uniformDef: UI_GRADIENT_LAYOUT,
        constants: { '0': aa },
        label,
      });
    this.gradientPipeline = make(1, 'UIBatch-gradient');
    this.gradientPipelineHard = make(0, 'UIBatch-gradient-hard');
  }

  private initImagePipelines(): void {
    const wgsl = getCompiledShader('ui/ui-image');
    this.imageShader = this.ctx.createShader(wgsl, wgsl);
    const make = (aa: number, label: string) =>
      this.ctx.createPipeline({
        shader: this.imageShader,
        vertexBuffers: [SPRITE_VERTEX_LAYOUT],
        colorTargets: [{ format: this.ctx.getSwapChainFormat() }],
        primitive: { usesTextures: true, textureCount: 1 },
        constants: { '0': aa },
        label,
      });
    this.imagePipeline = make(1, 'UIBatch-image');
    this.imagePipelineHard = make(0, 'UIBatch-image-hard');
  }

  /** Pipeline pair for a material guid; null while WGSL is unresolved (retry) or after a failure. */
  private materialPipelineFor(guid: string): MaterialPipelines | null {
    const cached = this.materialPipelines.get(guid);
    if (cached === 'failed') return null;
    if (cached) return cached;
    const wgsl = this.resolveMaterialWGSL?.(guid) ?? null;
    if (!wgsl) return null; // NOT cached — the host may still be loading; retry next frame
    try {
      const shader = this.ctx.createShader(wgsl, wgsl);
      const make = (aa: number, label: string) =>
        this.ctx.createPipeline({
          shader,
          vertexBuffers: [SPRITE_VERTEX_LAYOUT],
          colorTargets: [{ format: this.ctx.getSwapChainFormat() }],
          primitive: { usesTextures: false },
          constants: { '0': aa }, // RP_UIMAT_ANTIALIAS @id(0) (see prepareUIMaterialModule)
          label,
        });
      const entry: MaterialPipelines = {
        shader,
        aa: make(1, `UIBatch-mat-${guid}`),
        hard: make(0, `UIBatch-mat-${guid}-hard`),
      };
      this.materialPipelines.set(guid, entry);
      return entry;
    } catch (e) {
      console.warn(`[ui] UI material '${guid}' pipeline creation failed — drawing plain rects`, e);
      this.materialPipelines.set(guid, 'failed');
      return null;
    }
  }

  /**
   * Write the rounded overflow-clip uniforms for the CURRENT batch (must run before captureUniforms).
   * When the active scissor carries a corner radius, the UI shaders' `uiClip` carves the rounded
   * corners on top of the hardware scissor's rectangular cut; otherwise the enabled flag is 0 and
   * the clip is a no-op. Written every flush so a previous batch's clip never bleeds through the
   * shared uniform data (setUniform silently skips these names for pipelines that lack the slots).
   */
  /**
   * Ensure the inverse-homography uniform matches THIS perspective quad's (transform, center). A
   * change flushes the prior batch (so it emits under its own uniform) then rebuilds. Returns false
   * when the projection is degenerate (edge-on tilt) — the caller skips the quad. Same-node quads
   * with an identical (transform, center) — e.g. fill + border — reuse it and batch together.
   */
  private ensureHomography(cx: number, cy: number): boolean {
    const t = this.transform!;
    if (this.hom && this.homTransform === t && this.homCx === cx && this.homCy === cy) return true;
    this.flush();
    // Hc: centered-local (lx,ly) → screen = transform ∘ translate(center); invert for screen→local.
    const inv = invert(multiply(t, { a: 1, b: 0, c: 0, d: 1, e: cx, f: cy, g: 0, h: 0 }));
    if (!inv) {
      this.hom = null;
      this.homTransform = null;
      return false;
    }
    // columns acting on (screenX, screenY, 1): local = (uHom0·sx + uHom1·sy + uHom2).xy / .z
    this.homU0[0] = inv.a; this.homU0[1] = inv.b; this.homU0[2] = inv.g; this.homU0[3] = 1;
    this.homU1[0] = inv.c; this.homU1[1] = inv.d; this.homU1[2] = inv.h; this.homU1[3] = 0;
    this.homU2[0] = inv.e; this.homU2[1] = inv.f; this.homU2[2] = 1; this.homU2[3] = 0;
    this.hom = this.homU0;
    this.homTransform = t;
    this.homCx = cx;
    this.homCy = cy;
    return true;
  }

  /** Leave the perspective path: flush the 3D quads under their uniform, then clear the flag. */
  private clearHomography(): void {
    if (!this.hom) return;
    this.flush();
    this.hom = null;
    this.homTransform = null;
  }

  /** Write the inverse-homography uniforms for the flush (flag off when no perspective quad is active). */
  private applyHomography(): void {
    if (this.hom) {
      this.ctx.setUniform('uHom0', this.homU0);
      this.ctx.setUniform('uHom1', this.homU1);
      this.ctx.setUniform('uHom2', this.homU2);
    } else {
      this.ctx.setUniform('uHom0', UIBatch.HOM_OFF);
    }
  }

  private applyClip(): void {
    const s = this.scissor;
    if (s && s.radius && s.radius > 0) {
      this.clipRectU[0] = s.x + s.width / 2;
      this.clipRectU[1] = s.y + s.height / 2;
      this.clipRectU[2] = s.width / 2;
      this.clipRectU[3] = s.height / 2;
      this.clipParamsU[0] = s.radius;
      this.clipParamsU[1] = 1; // enabled
      this.ctx.setUniform('uClipRect', this.clipRectU);
      this.ctx.setUniform('uClipParams', this.clipParamsU);
    } else {
      this.ctx.setUniform('uClipParams', UIBatch.CLIP_OFF);
    }
  }

  private flush(): void {
    const quads = this.quadCount - this.batchStart;
    if (quads === 0) return;
    const { ctx } = this;

    const byteOffset = this.batchStart * VERTS_PER_QUAD * SPRITE_VERTEX_SIZE;
    const floatStart = byteOffset / 4;
    const floatCount = quads * VERTS_PER_QUAD * (SPRITE_VERTEX_SIZE / 4);
    ctx.updateBuffer(this.vertexBuffer, this.vertexData.subarray(floatStart, floatStart + floatCount), byteOffset);

    if (this.scissor) {
      ctx.setScissor(this.scissor.x, this.scissor.y, this.scissor.width, this.scissor.height);
    } else {
      ctx.disableScissor();
    }
    // Every UI mode SNAPSHOTS its uniforms (captureUniforms) — the rounded-clip (applyClip) is a
    // per-batch uniform, so the shared buffer would otherwise leak one batch's clip into the next
    // (rect/shadow used to ride the shared buffer with only the pass-constant viewProjection). The
    // clip write must precede captureUniforms so it lands in the snapshot.
    if (this.mode === 'text') {
      // Shared-uniform contract (see PostProcess overhaul): write uniforms → snapshot → THEN bind
      // textures (the texture bind group captures the uniform buffer at creation). Without the
      // snapshot, multiple fonts per frame would all read the LAST flush's pxRange.
      ctx.setPipeline(this.textPipeline);
      ctx.setUniform('uViewProjection', this.vp);
      // pxRange rides the default uniform-layout's 'uTime' slot (float @ byte 64) — see text-msdf.slang
      ctx.setUniform('uTime', this.fontPxRange);
      this.applyClip();
      ctx.captureUniforms?.();
      ctx.bindTexture(0, this.fontTexture);
    } else if (this.mode === 'backdrop') {
      ctx.setPipeline(this.antialias ? this.backdropPipeline : this.backdropPipelineHard);
      ctx.setUniform('uViewProjection', this.vp);
      ctx.captureUniforms?.();
      ctx.bindTexture(0, this.backdropTexture);
    } else if (this.mode === 'image') {
      ctx.setPipeline(this.antialias ? this.imagePipeline : this.imagePipelineHard);
      ctx.setUniform('uViewProjection', this.vp);
      this.applyClip();
      this.applyHomography();
      ctx.captureUniforms?.();
      ctx.bindTexture(0, this.imageTexture);
    } else if (this.mode === 'shadow') {
      ctx.setPipeline(this.shadowInset ? this.shadowPipelineInset : this.shadowPipeline);
      ctx.setUniform('uViewProjection', this.vp);
      this.applyClip();
      this.applyHomography();
      ctx.captureUniforms?.();
    } else if (this.mode === 'gradient') {
      ctx.setPipeline(this.antialias ? this.gradientPipeline : this.gradientPipelineHard);
      ctx.setUniform('uViewProjection', this.vp);
      ctx.setUniform('uGradHeader', this.gradHeader);
      for (let i = 0; i < GRADIENT_STOPS; i++) {
        ctx.setUniform(`uGradColor${i}`, this.gradColors.subarray(i * 4, i * 4 + 4) as Float32Array);
      }
      ctx.setUniform('uGradPosA', this.gradPos.subarray(0, 4) as Float32Array);
      ctx.setUniform('uGradPosB', this.gradPos.subarray(4, 8) as Float32Array);
      ctx.setUniform('uGradShape', this.gradShape);
      this.applyClip();
      this.applyHomography();
      ctx.captureUniforms?.(); // per-draw payload — isolate from the next gradient's writes
    } else if (this.mode === 'material') {
      const mat = this.materialPipelines.get(this.materialGuid);
      if (!mat || mat === 'failed') {
        // pipeline died between materialRect and flush (invalidate mid-frame) — drop the quads
        this.batchStart = this.quadCount;
        return;
      }
      ctx.setPipeline(this.antialias ? mat.aa : mat.hard);
      ctx.setUniform('uViewProjection', this.vp);
      // uTime = UIDocument clock (shader animations); snapshot isolates per-canvas time
      ctx.setUniform('uTime', this.materialTime);
      this.applyClip();
      this.applyHomography();
      ctx.captureUniforms?.();
    } else {
      ctx.setPipeline(this.antialias ? this.rectPipeline : this.rectPipelineHard);
      ctx.setUniform('uViewProjection', this.vp);
      this.applyClip();
      this.applyHomography();
      ctx.captureUniforms?.();
    }
    ctx.bindVertexBuffer(this.vertexBuffer);
    ctx.setupVertexAttributes(SPRITE_VERTEX_LAYOUT);
    ctx.bindIndexBuffer(this.indexBuffer);
    ctx.drawIndexed(quads * INDICES_PER_QUAD, this.batchStart * INDICES_PER_QUAD);

    this._drawCalls++;
    if (this.quadCount >= MAX_QUADS) {
      this.quadCount = 0;
      this.batchStart = 0;
    } else {
      this.batchStart = this.quadCount;
    }
  }

  get drawCalls(): number {
    return this._drawCalls;
  }

  /** Drop the cached GPU texture for a font (atlas changed — reimport/hot-reload). */
  invalidateFontTexture(fontGuid: string): void {
    const handle = this.fontTextures.get(fontGuid);
    if (handle === undefined) return;
    this.fontTextures.delete(fontGuid);
    this.ctx.destroyTexture(handle);
  }

  /** Drop the cached GPU texture for a background image (reimport/hot-reload). */
  invalidateImageTexture(imageGuid: string): void {
    const entry = this.imageTextures.get(imageGuid);
    if (entry === undefined) return;
    this.imageTextures.delete(imageGuid);
    this.ctx.destroyTexture(entry.texture);
  }

  /** Drop a material's pipelines (shader reimported) — recreated from fresh WGSL next frame.
   *  Also clears a 'failed' mark so a fixed shader gets retried. */
  invalidateMaterial(materialGuid: string): void {
    const entry = this.materialPipelines.get(materialGuid);
    this.materialPipelines.delete(materialGuid);
    if (!entry || entry === 'failed') return;
    this.ctx.destroyPipeline(entry.aa);
    this.ctx.destroyPipeline(entry.hard);
    this.ctx.destroyShader(entry.shader);
  }

  destroy(): void {
    this.ctx.destroyBuffer(this.vertexBuffer);
    this.ctx.destroyBuffer(this.indexBuffer);
    if (this.rectPipeline !== NULL_HANDLE) this.ctx.destroyPipeline(this.rectPipeline);
    if (this.rectPipelineHard !== NULL_HANDLE) this.ctx.destroyPipeline(this.rectPipelineHard);
    if (this.rectShader !== NULL_HANDLE) this.ctx.destroyShader(this.rectShader);
    if (this.textPipeline !== NULL_HANDLE) this.ctx.destroyPipeline(this.textPipeline);
    if (this.textShader !== NULL_HANDLE) this.ctx.destroyShader(this.textShader);
    if (this.backdropPipeline !== NULL_HANDLE) this.ctx.destroyPipeline(this.backdropPipeline);
    if (this.backdropPipelineHard !== NULL_HANDLE) this.ctx.destroyPipeline(this.backdropPipelineHard);
    if (this.backdropShader !== NULL_HANDLE) this.ctx.destroyShader(this.backdropShader);
    if (this.shadowPipeline !== NULL_HANDLE) this.ctx.destroyPipeline(this.shadowPipeline);
    if (this.shadowPipelineInset !== NULL_HANDLE) this.ctx.destroyPipeline(this.shadowPipelineInset);
    if (this.shadowShader !== NULL_HANDLE) this.ctx.destroyShader(this.shadowShader);
    if (this.gradientPipeline !== NULL_HANDLE) this.ctx.destroyPipeline(this.gradientPipeline);
    if (this.gradientPipelineHard !== NULL_HANDLE) this.ctx.destroyPipeline(this.gradientPipelineHard);
    if (this.gradientShader !== NULL_HANDLE) this.ctx.destroyShader(this.gradientShader);
    if (this.imagePipeline !== NULL_HANDLE) this.ctx.destroyPipeline(this.imagePipeline);
    if (this.imagePipelineHard !== NULL_HANDLE) this.ctx.destroyPipeline(this.imagePipelineHard);
    if (this.imageShader !== NULL_HANDLE) this.ctx.destroyShader(this.imageShader);
    for (const guid of [...this.materialPipelines.keys()]) this.invalidateMaterial(guid);
    this.backdrop?.destroy();
    this.backdrop = null;
    for (const handle of this.fontTextures.values()) this.ctx.destroyTexture(handle);
    this.fontTextures.clear();
    for (const entry of this.imageTextures.values()) this.ctx.destroyTexture(entry.texture);
    this.imageTextures.clear();
  }
}
